import React from 'react'
import clsx from 'clsx'
import { Logger } from 'wdc-cube'
import { makeStyles } from 'tss-react/mui'

import CloseFullscreenRoundedIcon from '@mui/icons-material/CloseFullscreenRounded'
import DownloadIcon from '@mui/icons-material/Download'
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded'
import Tooltip from '@mui/material/Tooltip'
import {
    DataGridProps,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridSortModel,
    GridValidRowModel
} from '@mui/x-data-grid'

import {
    CardIconButton,
    LifesHubCard,
    LifesHubCardBody,
    LifesHubCardHeader,
    LifesHubEmptyState,
    WorkingBar
} from 'src/utils/views'
import { FCClassContext, classToFComponent } from 'src/utils/views'
import { formatCNPJ, formatNumber } from 'src/utils/formatter-utils'

import { CompanyCardScope, type CompanyRow } from '../ta_scopes'
import { type CompanySortModel } from '../ta_types'
import { ROWS_PER_PAGE_OPTIONS, TaDataGrid, TaPagination, TaSkeleton } from 'src/utils/views/modules/the-acting'

const LOG = Logger.get('TA-VIEW-CO-CARD')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        alignSelf: 'stretch',
        flexGrow: 1,
        minHeight: 0
    },
    table: {
        alignSelf: 'stretch',
        flex: 1,
        flexBasis: 240,
        minHeight: 0
    }
})

const columns = static_buildColumns()

export type CompanyCardViewProps = {
    className?: string
    scope: CompanyCardScope
}

class CompanyCardViewClass implements FCClassContext<CompanyCardViewProps> {
    // :: Fields

    scope!: CompanyCardScope
    dataGrid: Partial<DataGridProps> = {
        initialState: {
            sorting: {
                sortModel: []
            }
        },
        pageSizeOptions: [100]
    }

    // :: Emissors

    readonly emitExpandClick = () => this.scope.onExpandClick().catch(LOG.caught)
    readonly emitShrunkClick = () => this.scope.onShrunkClick().catch(LOG.caught)
    readonly emitDownloadClick = () => this.scope.onDownloadClick().catch(LOG.caught)

    readonly emitRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const pageSize = parseInt(e.target.value, 10)
        this.scope.onPageSizeChange(pageSize).catch(LOG.caught)
    }

    readonly emitChangePage = (__e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        this.scope.onPageChange(newPage).catch(LOG.caught)
    }

    readonly emitRowClick = (params: GridRowParams<CompanyRow>) => {
        this.scope.onRowClick(params.row).catch(LOG.caught)
    }

    readonly emitSortModelChange = (gridSortModel: GridSortModel) => {
        const data: CompanySortModel[] = []
        for (const item of gridSortModel) {
            data.push({
                field: item.field as CompanySortModel['field'],
                sort: item.sort ?? 'asc'
            })
        }
        this.scope.onSortModelChange(data).catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: CompanyCardViewProps) {
        this.scope = scope

        // Evita repintar a grade por motivo de diferenca de atributo
        if (this.dataGrid.initialState?.sorting?.sortModel !== scope.sortBy) {
            this.dataGrid.initialState = {
                sorting: {
                    sortModel: scope.sortBy
                }
            }
        }

        if (this.dataGrid.pageSizeOptions?.at(0) !== scope.pageSize) {
            this.dataGrid.pageSizeOptions = [scope.pageSize]
        }
    }

    render({ className, scope }: CompanyCardViewProps) {
        const classes = useStyles().classes

        const empty = scope.rows.length === 0

        return (
            <LifesHubCard className={clsx(classes.view, className)} elevation={0}>
                <LifesHubCardHeader
                    title="Empresas e estabelecimentos com vínculo"
                    count={scope.total > 0 ? formatCompanyCount(scope.total) : undefined}
                    actions={
                        <>
                            {scope.downloadEnabled && (
                                <Tooltip title="Exportar empresas">
                                    <CardIconButton aria-label="Exportar empresas" onClick={this.emitDownloadClick}>
                                        <DownloadIcon />
                                    </CardIconButton>
                                </Tooltip>
                            )}
                            <Tooltip title={scope.expanded ? 'Restaurar card' : 'Maximizar card'}>
                                <CardIconButton
                                    aria-label={scope.expanded ? 'Restaurar card' : 'Maximizar card'}
                                    onClick={scope.expanded ? this.emitShrunkClick : this.emitExpandClick}
                                >
                                    {scope.expanded && <CloseFullscreenRoundedIcon />}
                                    {!scope.expanded && <LaunchRoundedIcon />}
                                </CardIconButton>
                            </Tooltip>
                        </>
                    }
                />
                <LifesHubCardBody>
                    {empty && scope.working && <TaSkeleton aria-hidden="true" />}
                    {empty && !scope.working && (
                        <LifesHubEmptyState
                            title="Nenhuma empresa no recorte"
                            text="Busque na barra superior ou remova um filtro do painel lateral para trazer empresas e estabelecimentos com vínculo."
                        />
                    )}
                    {!empty && (
                        <TaDataGrid
                            className={classes.table}
                            rows={scope.rows}
                            columns={columns}
                            sortingMode="server"
                            paginationMode="client"
                            pageSizeOptions={this.dataGrid.pageSizeOptions}
                            getRowId={static_getRowId}
                            disableVirtualization
                            hideFooter
                            hideFooterPagination
                            disableColumnFilter={true}
                            onRowClick={this.emitRowClick}
                            onSortModelChange={this.emitSortModelChange}
                            initialState={this.dataGrid.initialState}
                        />
                    )}
                    {scope.working && <WorkingBar />}
                    {scope.total > scope.pageSize && (
                        <TaPagination
                            component="div"
                            count={scope.total}
                            page={scope.page}
                            onPageChange={this.emitChangePage}
                            rowsPerPage={scope.pageSize}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={this.emitRowsPerPageChange}
                        />
                    )}
                </LifesHubCardBody>
            </LifesHubCard>
        )
    }
}
export const CompanyCardView = classToFComponent(CompanyCardViewClass, React)
export default CompanyCardView

function static_getRowId(row: GridValidRowModel) {
    const { cnes, cnpj } = row as CompanyRow
    return `${cnes ?? '0000000'}-${cnpj ?? '00000000000000'}`
}

function formatCompanyCount(total: number) {
    return total === 1 ? '1 empresa encontrada' : `${formatNumber(total)} empresas encontradas`
}

function static_buildColumns(): GridColDef[] {
    return [
        {
            field: 'cnes',
            headerName: 'CNES',
            width: 80
        },
        {
            field: 'cnpj',
            headerName: 'CNPJ',
            width: 160,
            valueFormatter: (value: string) => {
                return value ? formatCNPJ(value) : ''
            }
        },
        {
            field: 'nome',
            headerName: 'Nome',
            width: 250,
            renderCell: (params: GridRenderCellParams) => <CellTitle value={params.value} />
        },
        {
            field: 'cnae',
            headerName: 'CNAE',
            width: 80
        },
        {
            field: 'descricaoCnae',
            headerName: 'Descrição CNAE',
            width: 250
        },
        {
            field: 'naturezaJuridicaDesc',
            headerName: 'Natureza jurídica / agrupamento',
            width: 300
        },
        {
            field: 'esTipoNome',
            headerName: 'Tipo de estab. de saúde',
            width: 250
        },
        {
            field: 'endereco',
            headerName: 'Endereço',
            width: 500
        },
        {
            field: 'enderecoMunicipio',
            headerName: 'Município',
            width: 120
        },
        {
            field: 'siglaUF',
            headerName: 'UF',
            width: 40
        },
        {
            field: 'qtdeFuncionarios',
            headerName: 'Funcionários',
            width: 120,
            type: 'number'
        },
        {
            field: 'qtdLeitos',
            headerName: 'Nº de leitos',
            width: 120,
            type: 'number'
        },
        {
            field: 'complexidade',
            headerName: 'Complexidade',
            width: 280
        }
    ]
}

function CellTitle({ value }: { value?: unknown }) {
    const label = value == null ? '' : String(value)
    return (
        <span style={{ fontWeight: 750, overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
            {label}
        </span>
    )
}
